import { useRef, useState } from "react";
import type { ChangeEvent } from "react";
import { MAX_UPLOAD_BYTES } from "@eaub/shared";
import { useApi } from "../api/ApiProvider";
import { putToPresignedUrl } from "../api/client";
import { formatBytes } from "../lib/format";
import { ErrorBanner } from "./ErrorBanner";

interface UploadControlProps {
  onUploaded: () => void;
  label?: string;
}

export function UploadControl({ onUploaded, label = "Upload file" }: UploadControlProps) {
  const api = useApi();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);

  const onPick = () => inputRef.current?.click();

  const onChange = async (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    // Allow re-selecting the same file later.
    event.target.value = "";
    if (!file) return;

    setError(undefined);

    if (file.size > MAX_UPLOAD_BYTES) {
      setError(`File is too large (max ${formatBytes(MAX_UPLOAD_BYTES)}).`);
      return;
    }

    const contentType = file.type || "application/octet-stream";
    setUploading(true);
    try {
      const presign = await api.presignMedia({
        filename: file.name,
        contentType,
        size: file.size,
      });
      await putToPresignedUrl(presign.uploadUrl, file, contentType);
      onUploaded();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Upload failed.");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="upload-control">
      <input
        ref={inputRef}
        type="file"
        className="sr-only"
        onChange={(event) => void onChange(event)}
      />
      <button type="button" className="btn btn--primary" onClick={onPick} disabled={uploading}>
        {uploading ? "Uploading…" : label}
      </button>
      {error ? <ErrorBanner title="Upload error" message={error} /> : null}
    </div>
  );
}
